import type { ItemFieldRow } from './item-field-reader';
import { memoFromBaseRead } from './from-base-read-memo';

export interface DefaultProviderDependencies {
    /** Exact ordered ItemLayerTypeCatalog::defaultProviderNames() result for this compile context. */
    providerNames: readonly string[];
    /** Previously resolved definition or known miss. Unavailable references must throw. */
    resolveCarrierRef: (id: string) => ItemFieldRow | null;
    /** Exact _ft_node_item_id result; separate from item_field's source-table identity bridges. */
    nodeItemId: (row: ItemFieldRow) => string | null;
}

function arrayLike(value: unknown): value is ItemFieldRow {
    return value !== null && typeof value === 'object';
}
function has(value: unknown, key: string): boolean {
    return arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
}
function own(value: unknown, key: string): unknown {
    return has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
}
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function entries(slot: ItemFieldRow): unknown[] {
    return has(slot, '0') && own(slot, 'items') == null && own(slot, 'item_layer_types') == null ? Object.values(slot) : [slot];
}
function subtypeRank(carrier: ItemFieldRow): number {
    const raw = own(own(carrier, 'items'), 'item_type');
    const kind = arrayLike(raw) ? own(raw, '0') : raw;
    // Only these literal string values can match PHP's string-cast match arms.
    return kind === 'creature_lineage' ? 0 : kind === 'creature_grouping' || kind === 'creature_sub_type_grouping' ? 2 : 1;
}
const pcSlots = ['ancestry_1', 'ancestry_2', 'lineage', 'background',
    ...Array.from({ length: 20 }, (_, index) => `class_${index + 1}`),
    ...Array.from({ length: 20 }, (_, index) => `subclass_${index + 1}`)];

/** One compile/request owns this reader and its ancestry-strip memo. Start a new
 * context for changed definitions; do not reuse it as an actor or global cache.
 * The generator preserves PHP's lazy resolution and yields shared read values.
 */
export function createDefaultProviderReader(dependencies: DefaultProviderDependencies): {
    providers: (row: ItemFieldRow | null) => Generator<ItemFieldRow>;
} {
    const memo = new Map<string, ItemFieldRow>();
    const shapes = new WeakMap<object, ItemFieldRow>();
    const names = [...dependencies.providerNames];
    // PHP's provider values use [] for empty/numeric array maps. Preserve that
    // read shape by copying only changed paths, and reuse immutable adaptations.
    function shared(value: unknown): unknown {
        if (!arrayLike(value)) return value ?? null;
        const cached = shapes.get(value);
        if (cached) return cached;
        // Frozen definitions recur through many stage-owned readers. Their JSON
        // adaptation is pure and can be shared for this compile's whole scope.
        if (Object.isFrozen(value)) {
            const out = memoFromBaseRead(value, 'provider-json-shape', () => adapt(value));
            shapes.set(value, out);
            return out;
        }
        return adapt(value);
    }
    function adapt(value: ItemFieldRow): ItemFieldRow {
        const entries = Object.entries(value);
        const list = entries.every(([key], index) => key === String(index));
        let out: ItemFieldRow = list && !Array.isArray(value) ? [] : value;
        for (const [key, child] of entries) {
            const next = shared(child);
            if (next === child && out === value) continue;
            if (out === value) out = Array.isArray(value) ? value.slice() : { ...value };
            (out as Record<string, unknown>)[key] = next;
        }
        if (Object.isFrozen(value)) {
            if (out !== value) Object.freeze(out);
            shapes.set(value, out);
        }
        return out;
    }
    function strip(carrier: ItemFieldRow, shape: 'ref' | 'nest'): ItemFieldRow {
        const id = dependencies.nodeItemId(carrier) ?? '';
        const key = `${id}|${shape}`;
        if (id !== '' && memo.has(key)) return memo.get(key)!;
        const stripped = id !== '' && Object.isFrozen(carrier)
            ? memoFromBaseRead(carrier, `provider-strip:${shape}`, build) : build();
        if (id !== '') memo.set(key, stripped);
        return stripped;
        function build(): ItemFieldRow {
            const stripped = { ...carrier } as Record<string, unknown>;
            const layers = own(stripped, 'item_layer_types');
            if (arrayLike(layers)) {
                const ownLayers = { ...layers };
                delete (ownLayers as Record<string, unknown>).passive_ability;
                stripped.item_layer_types = Object.isFrozen(layers) ? Object.freeze(ownLayers) : ownLayers;
            }
            return Object.isFrozen(carrier) ? Object.freeze(stripped) : stripped;
        }
    }
    function choicesResolved(pc: ItemFieldRow): boolean {
        for (const slot of ['ancestry_choice_1', 'ancestry_choice_2', 'ancestry_choice_3']) {
            const raw = own(pc, slot);
            const choice = arrayLike(raw) ? dependencies.nodeItemId(raw) : raw;
            if (!validUuid(choice)) return false;
        }
        return true;
    }
    function* providers(row: ItemFieldRow | null): Generator<ItemFieldRow> {
        if (row === null) return;
        const layers = own(row, 'item_layer_types');
        if (arrayLike(layers)) {
            for (const name of names) {
                const slot = own(layers, name);
                if (validUuid(slot)) {
                    const resolved = dependencies.resolveCarrierRef(slot);
                    if (resolved !== null) yield shared(resolved) as ItemFieldRow;
                    continue;
                }
                if (!arrayLike(slot)) continue;
                const list = entries(slot);
                if (name === 'creature_sub_type' && list.length > 1) {
                    const ranked: { carrier: ItemFieldRow; rank: number; sequence: number }[] = [];
                    for (const entry of list) {
                        const carrier = arrayLike(entry) ? entry : validUuid(entry) ? dependencies.resolveCarrierRef(entry) : null;
                        if (carrier !== null) ranked.push({ carrier, rank: subtypeRank(carrier), sequence: ranked.length });
                    }
                    ranked.sort((left, right) => left.rank - right.rank || left.sequence - right.sequence);
                    for (const { carrier } of ranked) yield shared(carrier) as ItemFieldRow;
                    continue;
                }
                for (const entry of list) {
                    if (arrayLike(entry)) yield shared(entry) as ItemFieldRow;
                    else if (validUuid(entry)) {
                        const resolved = dependencies.resolveCarrierRef(entry);
                        if (resolved !== null) yield shared(resolved) as ItemFieldRow;
                    }
                }
            }
        }
        const pc = own(row, 'player_characters');
        if (!arrayLike(pc)) return;
        const choiceGate = choicesResolved(pc);
        for (const slotName of pcSlots) {
            const slot = own(pc, slotName);
            const drop = choiceGate && (slotName === 'ancestry_1' || slotName === 'ancestry_2');
            if (validUuid(slot)) {
                const resolved = dependencies.resolveCarrierRef(slot);
                if (resolved !== null) yield shared(drop ? strip(resolved, 'ref') : resolved) as ItemFieldRow;
                continue;
            }
            if (!arrayLike(slot)) continue;
            for (const entry of entries(slot)) {
                if (arrayLike(entry) && (own(entry, 'items') != null || own(entry, 'item_layer_types') != null)) {
                    yield shared(drop ? strip(entry, 'nest') : entry) as ItemFieldRow;
                }
            }
        }
    }
    return { providers };
}
